/*
 * Assign flow model — canon §7.3 (`assign.flow.*`), transcribed from the
 * App-shell canon + the DC source (App Shell.dc.html assignVals). Pure — the
 * screen (AssignFlow.tsx) renders these; Vitest proves them against the canon
 * contract.
 *
 * DYNAMIC STEP LIST (canon §7.3 contract, from source):
 *   self                       → who, confirm
 *   child-on-account           → who, details, confirm
 *   someone else, banded minor-possible
 *     DOB ⇒ adult              → who, age, details, confirm
 *     DOB ⇒ minor              → who, age, guardian, details, confirm
 *   adult-only / unbanded      → who, details, confirm
 *   `Step {n} of {N}` always shows the true count; done shows `ASSIGNED`.
 *
 * AGE BANDS belong to the TICKET TYPE (organiser-set in manage.trumin) — never
 * the UI (§7.3 must-not). The signing age varies by the event's jurisdiction —
 * `SIGNING_AGE = 18` was a DC prototype constant and MUST NOT ship (§11.8);
 * both arrive through TicketPolicy (see resolveTicketPolicy).
 *
 * DRAFT-SCOPING RULE (§7.3 contract): "draft is reset AND stamped with the
 * seat — a stale answer can never apply to another place."
 */

export type AssignStepKey = 'who' | 'age' | 'guardian' | 'details' | 'confirm'
export type AssignMode = 'me' | 'other' | 'child'
export type GuardianMode = 'full' | 'shared'

export interface AgeBand { min: number; max: number }

/** Organiser/jurisdiction policy for one ticket type. */
export interface TicketPolicy {
  /** Age band of the ticket type — organiser-configured in manage.trumin. */
  band: AgeBand | null
  /** Legal signing age for the EVENT's jurisdiction. null ⇒ unknown. */
  signingAge: number | null
}

/**
 * Live policy seam. Today's my-api/box-office order payloads carry NO ticket
 * age-band and no jurisdiction, so live tickets resolve unbanded (adult
 * email-invite path only) — honestly reflecting what the platform knows.
 *
 * // TODO(organiser ticket-type config): band comes from the ticket type
 * // (manage.trumin) once the orders payload carries it — never inferred from
 * // the type NAME in shipped code (§7.3 must-not: bands from the ticket
 * // type, organiser-configured, never from the UI).
 * // TODO(jurisdiction, §7.3): signingAge from the event's jurisdiction —
 * // `SIGNING_AGE = 18` was DC-prototype-only and must not ship (§11.8).
 */
export function resolveTicketPolicy(): TicketPolicy {
  return { band: null, signingAge: null }
}

export const minorOnly = (p: TicketPolicy): boolean =>
  p.band !== null && p.signingAge !== null && p.band.max < p.signingAge
export const minorPossible = (p: TicketPolicy): boolean =>
  p.band !== null && p.signingAge !== null && p.band.min < p.signingAge

// ---------------------------------------------------------------------------
// Gender eligibility (CMT-2026-08-11-010806 — self-assign guard)
//
// The platform has NO structured gender field on a ticket yet: the only signal
// is a token inside the ticket typeName STRING (e.g. "Solo · PRO · Female").
// So we parse CONSERVATIVELY and block ONLY a clear binary-opposite mismatch on
// the SELF-assign path (the holder's own Cognito `gender` is the only gender we
// know at assign time). False-blocking a valid participant — non-binary,
// self-described, unknown/empty, or an Open/Mixed ticket — is worse than the
// pre-existing gap, so EVERYTHING ambiguous resolves to allow.
//
// TODO(structured ticket gender): once the organiser ticket-type config carries
// a real gender restriction in the order payload (like the age-band TODO on
// resolveTicketPolicy), read that instead of the typeName string. Server-side
// (my-api) has the same string-only signal today and does no check — a
// defence-in-depth guard there is a separate follow-up.
// ---------------------------------------------------------------------------

/** Binary-gender token sets. Exact-token match only (never substring) — a
 *  typeName token must BE one of these, so "Mixed"/"Open"/"PRO" never match. */
const FEMALE_TOKENS = new Set(['female', 'women', 'woman'])
const MALE_TOKENS = new Set(['male', 'men', 'man'])

/** The ticket's binary gender restriction parsed from its typeName, or null.
 *  Splits on '·', trims + lowercases each token, and returns 'female'/'male'
 *  ONLY when a token is EXACTLY a binary gender. Open / Mixed / absent / a
 *  contradictory mix (both a male AND a female token) → null (unrestricted). */
export function ticketGenderRestriction(typeName: string): 'male' | 'female' | null {
  if (!typeName) return null
  const tokens = typeName.split('·').map((t) => t.trim().toLowerCase())
  const female = tokens.some((t) => FEMALE_TOKENS.has(t))
  const male = tokens.some((t) => MALE_TOKENS.has(t))
  if (female && male) return null // contradictory → treat as unrestricted
  if (female) return 'female'
  if (male) return 'male'
  return null
}

/** Is the participant's own gender clearly the OPPOSITE binary of the ticket's
 *  restriction? Returns a participant-facing reason ONLY for a clear mismatch;
 *  everything else (unrestricted ticket, or unknown / empty / self-described /
 *  non-binary participant gender) allows. */
export function genderEligible(
  participantGender: string | null | undefined,
  typeName: string,
): { ok: boolean; reason?: string } {
  const restriction = ticketGenderRestriction(typeName)
  if (!restriction) return { ok: true }

  const g = (participantGender ?? '').trim().toLowerCase()
  if (!g) return { ok: true } // unknown → never block (conservative)
  const participantFemale = FEMALE_TOKENS.has(g)
  const participantMale = MALE_TOKENS.has(g)
  if (!participantFemale && !participantMale) return { ok: true } // self-describe / non-binary / other

  if (restriction === 'female' && participantMale) {
    return {
      ok: false,
      reason: "This ticket is for female participants; your profile gender doesn't match. Contact the organiser if this is wrong.",
    }
  }
  if (restriction === 'male' && participantFemale) {
    return {
      ok: false,
      reason: "This ticket is for male participants; your profile gender doesn't match. Contact the organiser if this is wrong.",
    }
  }
  return { ok: true }
}

// ---------------------------------------------------------------------------
// Draft (stamped with the seat)
// ---------------------------------------------------------------------------

export interface AssignDraft {
  /** The seat this draft belongs to — `{ticketId}.{seatNo}` (§7.3 scoping). */
  seatKey: string
  mode: AssignMode | ''
  // other-adult / guardian details
  email: string
  name: string
  // age step
  dobD: string
  dobM: string
  dobY: string
  // guardian step
  gMode: GuardianMode | ''
  teenEmail: string
  // child step
  child: string
  addKid: boolean
  kidFirst: string
  kidLast: string
}

/** Fresh draft for a seat — reset AND stamped (§7.3 draft-scoping rule).
 *  A minor-only band has only one lawful holder: mode is pre-set to `child`
 *  (the who step offers ONLY that choice). */
export function emptyDraft(seatKey: string, _policy: TicketPolicy): AssignDraft {
  return {
    seatKey,
    // CMT-033 (launch): no child pre-set — the child mode is removed for launch,
    // so even a minor-only ticket starts unset and the buyer picks myself/someone-else.
    // (_policy retained in the signature for call-site compatibility + when the
    // child-record flow ships a future day.)
    mode: '',
    email: '', name: '',
    dobD: '', dobM: '', dobY: '',
    gMode: '', teenEmail: '',
    child: '', addKid: false, kidFirst: '', kidLast: '',
  }
}

// ---------------------------------------------------------------------------
// Age (DC transcription: age on race day)
// ---------------------------------------------------------------------------

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export const dobComplete = (d: AssignDraft): boolean => !!(d.dobD && d.dobM && d.dobY)

/** Age on race day from the draft's Day/Month-name/Year selects. */
export function ageOnRaceDay(d: AssignDraft, raceDay: Date): number | null {
  if (!dobComplete(d)) return null
  const b = new Date(Number(d.dobY), MONTHS.indexOf(d.dobM), Number(d.dobD))
  return (
    raceDay.getFullYear() - b.getFullYear() -
    (raceDay.getMonth() < b.getMonth() ||
    (raceDay.getMonth() === b.getMonth() && raceDay.getDate() < b.getDate())
      ? 1
      : 0)
  )
}

export const isMinorAge = (age: number | null, p: TicketPolicy): boolean =>
  age !== null && p.signingAge !== null && age < p.signingAge
export const inBand = (age: number | null, p: TicketPolicy): boolean =>
  age === null || p.band === null || (age >= p.band.min && age <= p.band.max)

/** Age verdict box (§7.3): red outside band / amber minor / teal adult. */
export function ageVerdict(age: number | null, p: TicketPolicy): { text: string; tone: 'bad' | 'minor' | 'ok' } | null {
  if (age === null || p.band === null) return null
  if (!inBand(age, p)) {
    return { text: `${age} on race day — outside this ticket's ${p.band.min}–${p.band.max} range.`, tone: 'bad' }
  }
  if (isMinorAge(age, p)) {
    return { text: `${age} on race day — under ${p.signingAge}, so a parent or guardian is needed.`, tone: 'minor' }
  }
  return { text: `✓ ${age} on race day — old enough to sign for themselves.`, tone: 'ok' }
}

// ---------------------------------------------------------------------------
// Step machine
// ---------------------------------------------------------------------------

/** The DYNAMIC step list (§7.3 contract). */
export function stepList(d: AssignDraft, p: TicketPolicy, age: number | null): AssignStepKey[] {
  if (d.mode === 'me') return ['who', 'confirm']
  if (d.mode === 'child') return ['who', 'details', 'confirm']
  if (minorPossible(p)) {
    return isMinorAge(age, p)
      ? ['who', 'age', 'guardian', 'details', 'confirm']
      : ['who', 'age', 'details', 'confirm']
  }
  return ['who', 'details', 'confirm']
}

/** Counter `Step {n} of {N}` — true count; done shows `ASSIGNED` (§7.3). */
export function stepCounter(stepIndex: number, total: number, done: boolean): string {
  return done ? 'ASSIGNED' : `Step ${Math.min(stepIndex + 1, total)} of ${total}`
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
export const emailOk = (v: string): boolean => EMAIL_RE.test(v.trim())

/** Can the current step continue? (DC okA transcription.) */
export function stepOk(
  cur: AssignStepKey,
  d: AssignDraft,
  p: TicketPolicy,
  age: number | null,
  hasKids: boolean,
): boolean {
  const adding = d.addKid || !hasKids
  switch (cur) {
    case 'who': return !!d.mode
    case 'age': return dobComplete(d) && inBand(age, p)
    case 'guardian': return !!d.gMode && (d.gMode === 'full' || emailOk(d.teenEmail))
    case 'details':
      return d.mode === 'child'
        ? adding
          ? !!d.kidFirst.trim() && !!d.kidLast.trim()
          : !!d.child
        : emailOk(d.email)
    default: return true
  }
}

/** Block bar text (§7.3 [chrome], verbatim). */
export function blockText(cur: AssignStepKey, d: AssignDraft, p: TicketPolicy, hasKids: boolean): string {
  const adding = d.addKid || !hasKids
  switch (cur) {
    case 'who': return 'Choose who this place is for'
    case 'age':
      return !dobComplete(d)
        ? 'Enter their date of birth'
        : `They must be ${p.band?.min}–${p.band?.max} on race day for this ticket`
    case 'guardian':
      return !d.gMode ? 'Choose how their account is managed' : 'Enter their email address'
    default:
      return d.mode === 'child'
        ? adding
          ? "Enter the child's first and last name"
          : 'Choose which child'
        : 'Enter a valid email address'
  }
}

// ---------------------------------------------------------------------------
// Chrome copy (§7.3 [chrome], verbatim DC transcription)
// ---------------------------------------------------------------------------

export const EYEBROWS: Record<AssignStepKey, string> = {
  who: 'Who is this for',
  age: 'Their age',
  guardian: 'Guardian arrangement',
  details: 'Their details',
  confirm: 'Check and confirm',
}

/** `Person {i} of {n}` (multi-seat) / `Participant` (single) — flow header
 *  context + who intro (§7.3). */
export function seatLabel(seatNo: number, of: number): string {
  return of > 1 ? `Person ${seatNo} of ${of}` : 'Participant'
}

export interface AssignCtx {
  ticketType: string
  seatNo: number
  of: number
  /** Current holder label when re-assigning a filled seat ('' = fresh). */
  holder: string
  myName: string
  myEmail: string
  /** Signed-in account holder's Cognito `gender` (self-assign eligibility).
   *  Undefined until the profile loads / when the account has no gender set —
   *  genderEligible treats unknown as ALLOW (conservative). */
  myGender?: string
}

export function heading(
  cur: AssignStepKey, d: AssignDraft, ctx: AssignCtx, age: number | null, adding: boolean, isMinor: boolean,
): string {
  switch (cur) {
    case 'who': return ctx.holder ? 'Change who holds this place' : 'Who is taking this place?'
    case 'age': return 'When were they born?'
    case 'guardian': return `They're ${age} — how should this work?`
    case 'details':
      return d.mode === 'child'
        ? adding ? 'Add a child' : 'Which child?'
        : isMinor ? "Their guardian's details" : 'Where do we send the invite?'
    default:
      return d.mode === 'me' ? 'Confirm this is yours' : d.mode === 'child' ? 'Confirm this place' : 'Ready to send'
  }
}

export function intro(
  cur: AssignStepKey, d: AssignDraft, ctx: AssignCtx, p: TicketPolicy, isMinor: boolean,
): string {
  switch (cur) {
    case 'who':
      return `${ctx.ticketType} · ${seatLabel(ctx.seatNo, ctx.of)}. You can change this later if plans change.`
    case 'age':
      return `This ticket is for ages ${p.band ? `${p.band.min}–${p.band.max}` : ''}. Their age decides who signs the waiver.`
    case 'guardian':
      return `Under ${p.signingAge} they cannot sign a waiver or pay, but they can still run their own account. You choose.`
    case 'details':
      return d.mode === 'child'
        ? 'Children on your account never get their own login — you complete everything for them.'
        : isMinor
          ? `We'll email you as their guardian${d.gMode === 'shared' ? ' and send them their own login separately' : ''}.`
          : "They'll get an email invite and complete their own profile and waiver."
    default:
      return d.mode === 'me'
        ? 'This place will use your existing profile.'
        : d.mode === 'child'
          ? 'No invite is sent — you complete their details yourself.'
          : 'Nothing is sent until you confirm.'
  }
}

/** Re-assign warning (§7.3). */
export function holderNote(holder: string): string {
  return holder ? `This place is currently held by ${holder}. Choosing someone else replaces them.` : ''
}

/** Band note on the who step (§7.3): minor-only (child-tinted) / minors-possible. */
export function bandNote(p: TicketPolicy): { text: string; tone: 'child' | 'neutral' } | null {
  if (!p.band) return null
  if (minorOnly(p)) {
    return {
      text: `This ticket is for ages ${p.band.min}–${p.band.max}, so it must be held by a child on your account — a parent or guardian signs the waiver.`,
      tone: 'child',
    }
  }
  if (minorPossible(p)) {
    return {
      text: `This ticket is for ages ${p.band.min}–${p.band.max}. If they are under ${p.signingAge} you will sign the waiver and pay, whoever manages the account.`,
      tone: 'neutral',
    }
  }
  return null
}

export interface Choice { mode: AssignMode; label: string; note: string; icon: string }

/** Who-step choice cards (§7.3). A minor-only band offers ONLY the child
 *  choice (must-not: never offer Myself/Someone else on a minor-only ticket). */
export function choices(p: TicketPolicy, myName: string): Choice[] {
  // LAUNCH SCOPE (CMT-2026-08-14-033, Duncan live-test 2026-08-14): the "A child
  // on my account" assignment path is REMOVED for launch — the child-record flow
  // is broken and not fixable before Sunday; it ships a future day. Only the two
  // vanilla modes (Myself / Someone else) are offered, so the 'child' mode can
  // never be selected and its phantom "not available yet" warning can never show.
  // (The minor-only branch that used to return child-only now also falls back to
  // the vanilla options — a minor-only ticket is a rare edge not in the OnePulse
  // launch event; someone-else + the age step handles under-18 attribution.)
  return [
    { mode: 'me', label: `Myself — ${myName}`, note: 'Uses your existing profile and waiver', icon: '◉' },
    {
      mode: 'other',
      label: 'Someone else',
      note: minorPossible(p)
        ? "We'll ask their age next — under 18 is handled differently"
        : 'Send them an email invite to claim it',
      icon: '✉',
    },
  ]
}

/** Guardian-arrangement option cards (§7.3). */
export const GUARDIAN_OPTS: Array<{ gMode: GuardianMode; title: string; note: string; best: string }> = [
  {
    gMode: 'full',
    title: 'I manage it for them',
    note: 'No login for them. You complete their profile, sign the waiver and hold the ticket in your wallet.',
    best: 'Best for younger teens',
  },
  {
    gMode: 'shared',
    title: 'They get their own login',
    note: 'They manage their profile, wave and ticket. The waiver and any payment stay with you.',
    best: 'Best for 16–17s who want their own account',
  },
]

/** Responsibility-split cards (§7.3 — bullet lists verbatim, DC 2231–2238). */
export function splitCards(gMode: GuardianMode, myName: string, theirName: string): Array<{
  side: 'guardian' | 'them'
  who: string
  tag: string
  items: string[]
}> {
  return [
    {
      side: 'guardian',
      who: `${myName} (you)`,
      tag: 'GUARDIAN',
      items: gMode === 'shared'
        ? ['Signs the waiver', 'Pays and receives refunds', 'Named emergency contact', 'Can see and change everything']
        : ['Signs the waiver', 'Pays and receives refunds', 'Completes their profile', 'Holds their ticket'],
    },
    {
      side: 'them',
      who: theirName.trim() || 'They',
      tag: gMode === 'shared' ? 'OWN LOGIN' : 'NO LOGIN',
      items: gMode === 'shared'
        ? ['Own email and password', 'Edits their own profile', 'Sees their wave and ticket', 'Cannot sign or pay']
        : ['Nothing — you hold it all'],
    },
  ]
}

/** Details-step labels/hints (§7.3): other-adult vs minor-guardian variants. */
export function detailChrome(isMinor: boolean): {
  emailLabel: string; emailHint: string; nameLabel: string; namePlaceholder: string
} {
  return isMinor
    ? {
        emailLabel: "Guardian's email (yours)",
        emailHint: 'The waiver and receipts come here',
        nameLabel: "Participant's name",
        namePlaceholder: 'The name that goes on their bib',
      }
    : {
        emailLabel: 'Their email',
        emailHint: 'They claim the ticket with this address',
        nameLabel: 'Their name (optional)',
        namePlaceholder: 'So you can tell places apart',
      }
}

/** Live email hint under the input (§7.3 / DC): idle hint → ✓ / error. */
export function emailHintLine(value: string, idleHint: string): { text: string; tone: 'idle' | 'ok' | 'bad' } {
  if (!value) return { text: idleHint, tone: 'idle' }
  return emailOk(value)
    ? { text: '✓ Looks right', tone: 'ok' }
    : { text: 'That does not look like an email address', tone: 'bad' }
}

/** Add-a-child hint (§7.3). */
export function addChildHint(hasKids: boolean): string {
  return hasKids
    ? 'Add their name now; date of birth, category and kit come next.'
    : "You haven't added anyone yet — add them here and finish their details next."
}

// ---------------------------------------------------------------------------
// Confirm + done (§7.3 [chrome], verbatim)
// ---------------------------------------------------------------------------

export interface ManagedChildRef { key: string; name: string; profileComplete: boolean }

/** Who the seat is being assigned to, resolved from the draft. */
export function assigneeName(d: AssignDraft, ctx: AssignCtx, kids: ManagedChildRef[], isMinor: boolean): string {
  const adding = d.addKid || kids.length === 0
  const newKid = `${d.kidFirst.trim()} ${d.kidLast.trim()}`.trim()
  const chosen = kids.find((k) => k.key === d.child)
  const childName = adding ? newKid : chosen?.name ?? ''
  if (d.mode === 'me') return ctx.myName
  if (d.mode === 'child') return childName
  if (isMinor) return d.name.trim() || d.teenEmail.trim() || 'Under-18 participant'
  return d.email.trim()
}

/** Confirm summary rows (§7.3): Ticket · Place · Assigned to · How
 *  (+ minor-only Age on race day / Guardian contact / Waiver & payment,
 *  + shared-only Their login).
 *
 *  STEP 2 (dispatch BOARDROOM-2026-08-11-081455): the self ('me') "Confirm this
 *  is yours" screen DROPS the How row entirely ("Your own profile — nothing to
 *  send" is internal detail with no user value). The How row is retained for the
 *  other-adult / child / minor-guardian paths where it carries real meaning
 *  (email invite, child-on-account, guardian arrangement). */
export function summaryRows(
  d: AssignDraft, ctx: AssignCtx, p: TicketPolicy, age: number | null, kids: ManagedChildRef[],
): Array<{ k: string; v: string }> {
  const isMinor = d.mode === 'other' && isMinorAge(age, p)
  const adding = d.addKid || kids.length === 0
  const guardianContact = d.mode === 'me' ? ctx.myEmail : d.email.trim()
  const rows: Array<{ k: string; v: string } | null> = [
    { k: 'Ticket', v: ctx.ticketType },
    { k: 'Place', v: seatLabel(ctx.seatNo, ctx.of) },
    { k: 'Assigned to', v: assigneeName(d, ctx, kids, isMinor) },
    d.mode === 'me'
      ? null // self path: How row removed (STEP 2) — no user value
      : {
          k: 'How',
          v: d.mode === 'child'
            ? adding ? 'New child on your account' : 'You complete their details'
            : isMinor
              ? d.gMode === 'shared' ? 'Their own login, you as guardian' : 'You manage it for them'
              : 'Email invite',
        },
    isMinor ? { k: 'Age on race day', v: `${age} — under ${p.signingAge}` } : null,
    isMinor ? { k: 'Guardian contact', v: guardianContact } : null,
    isMinor ? { k: 'Waiver & payment', v: `${ctx.myName} (guardian)` } : null,
    isMinor && d.gMode === 'shared' ? { k: 'Their login', v: d.teenEmail.trim() } : null,
  ]
  return rows.filter((r): r is { k: string; v: string } => r !== null)
}

/** What-happens-next line (§7.3). */
export function nextNote(d: AssignDraft, isMinor: boolean): string {
  if (d.mode === 'me') return 'The ticket moves to your Get ready list.'
  if (d.mode === 'child') return "You'll fill in their profile and sign their waiver."
  if (isMinor) {
    return d.gMode === 'shared'
      ? 'Two emails go out: your guardian waiver to sign, and their login invite. Their place is held either way.'
      : 'The waiver comes to you to sign. They get nothing to do — you hold the ticket.'
  }
  return 'They get an email now. Until they accept, you can resend or reassign.'
}

/** Confirm button label (§7.3). */
export function confirmLabel(d: AssignDraft, isMinor: boolean): string {
  if (d.mode === 'other' && isMinor) {
    return d.gMode === 'shared' ? 'Send both invites' : 'Confirm & send me the waiver'
  }
  return d.mode === 'other' ? 'Send invite' : 'Confirm'
}

/** Done heading (§7.3): re-assign wins, then subject variants.
 *  `inviteEmailSent` gates every "an email went out" claim — the backend
 *  returns it honestly (my-api inviteEmailSent) and the UI must never claim a
 *  send that did not happen (e.g. SendGrid unprovisioned). The allocation still
 *  succeeded, so the "held/assigned" outcome is true; only the email claim is
 *  gated. */
export function doneHeading(
  d: AssignDraft, ctx: AssignCtx, isMinor: boolean, inviteEmailSent = true,
): string {
  if (ctx.holder) return 'Place reassigned'
  if (d.mode === 'other' && isMinor) {
    if (!inviteEmailSent) return 'Place held'
    return d.gMode === 'shared' ? 'Place held — two invites sent' : 'Place held — waiver on its way to you'
  }
  if (d.mode === 'other') return inviteEmailSent ? 'Invite sent' : 'Ticket assigned'
  return d.mode === 'child' ? 'Place reserved' : 'Assigned to you'
}

/** Done sub (§7.3, DC 2314–2321 verbatim for the sent branch; honest
 *  not-sent branch when `inviteEmailSent === false`). */
export function doneSub(
  d: AssignDraft, ctx: AssignCtx, kids: ManagedChildRef[], isMinor: boolean, inviteEmailSent = true,
): string {
  const who = assigneeName(d, ctx, kids, isMinor)
  const guardianInbox = d.email.trim()
  if (d.mode === 'other' && isMinor) {
    if (!inviteEmailSent) {
      return `${d.name.trim() || 'They'} now hold this place. The invite email couldn't be sent yet — you can resend it from the order.`
    }
    return d.gMode === 'shared'
      ? `${d.name.trim() || 'They'} can set up their own login at ${d.teenEmail.trim()}. Your guardian waiver is waiting in ${guardianInbox}.`
      : `Your guardian waiver is waiting in ${guardianInbox}. Nothing is needed from ${d.name.trim() || 'them'}.`
  }
  if (d.mode === 'other') {
    return inviteEmailSent
      ? `We've emailed ${who}. You'll see it here as soon as they accept.`
      : `${who} now holds this place, but the invite email couldn't be sent yet — you can resend it from the order.`
  }
  if (d.mode === 'child') {
    const name = who || 'Your child'
    return `${name} is on your account and holds this place. Finish their details in Profile → ${who || 'their name'}.`
  }
  return 'This place is yours. It now appears in Get ready.'
}
